import { Injectable, Injector } from '@angular/core'
import {
  CanActivate,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router'
import { Store } from '@ngrx/store'
import { Observable } from 'rxjs'

import { AuthManagerService } from '@services/auth/auth-manager.service'
import { State } from '@app/core/store'
import { MessagesService } from '@app/core/services/messages/message.service'
import { logOut } from '@app/auth/store/actions/auth.actions'
/**
 * This Guard decides if a route can be activated. If all guards return `true`, navigation continues. If any guard returns `false`,
 * navigation is cancelled. If any guard returns a `UrlTree`, the current navigation
 * is cancelled and a new navigation begins to the `UrlTree` returned from the guard.
 *
 * This class should not be modified directly, you can provide your own service which should extend the default one as follows:
 ```
  export class MyAuthGuardService extends AuthGuardService {
   ...
  }
 ```
 * And provide your class in the `core/custom-core.module.ts` file as follows:
 ```
  providers: [
   { provide: AuthGuardService, useClass: MyAuthGuardService }
  ]
 ```
 * */
@Injectable({
  providedIn: 'root',
})
export class AuthGuardService implements CanActivate {
  /**
   * Default constructor
   */
  constructor(
    public router: Router,
    private injector: Injector,
    private message: MessagesService,
    private authService: AuthManagerService,
    private store: Store<State>
  ) {}

  /**
   * can activate routing
   * @param {ActivatedRouteSnapshot} next
   * @return {boolean}
   */
  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | Observable<boolean> {
    return true
  }
  /**
   * Checks if the screen's route can be activated
   * @param {ActivatedRouteSnapshot} next
   * @param {boolean} isActive
   * @return {boolean}
   */
  private isScreenAuthorized(
    next: ActivatedRouteSnapshot,
    isActive: boolean
  ): boolean {
    try {
      const featureName = next.data.feature
      const useCaseName = next.data.useCase
      if (featureName && isActive) {
        const service = this.injector.get(`${featureName}PolicyService`)
        if (!service.isScreenAuthorized(useCaseName)) {
          // you are not allowed to open this screen
          this.message.openErrorMessage(
            'You are not allowed to open this screen'
          )
          isActive = false
        }
      }
    } catch (error) {
      // no policy service is provided for this feature
    }
    return isActive
  }
}
